import { FC, useMemo } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Code,
  HStack,
  Icon,
  IconButton,
  ListItem,
  Text,
  UnorderedList,
  useToast,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';

interface Props {
  quoteName?: string;
  error: string | null;
  title?: string; // Optional custom title for the alert header
  introKey?: string; // i18n key override for intro text
  listTitleKey?: string; // i18n key override for list title
  contactKey?: string; // i18n key override for footer/contact text
}

// Try to extract a readable list of error lines from various error payload shapes
const extractAnvaErrors = (errorString: string | null): string[] => {
  if (!errorString) return [];

  const collectFromUnknown = (value: unknown): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            // Common shapes: { message }, { description }, { detail }
            const obj = item as Record<string, unknown>;
            return (
              (obj.message as string) ||
              (obj.description as string) ||
              (obj.detail as string) ||
              ''
            );
          }
          return '';
        })
        .filter((s) => s && s.trim().length > 0) as string[];
    }
    if (typeof value === 'string') return value.split('\n');
    return [];
  };

  // Attempt to parse JSON first
  try {
    const parsedUnknown: unknown = JSON.parse(errorString);
    const asRecord = (v: unknown): Record<string, unknown> | null =>
      v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
    const parsed = asRecord(parsedUnknown);
    if (!parsed) throw new Error('not-an-object');
    // Direct errors array
    const direct = collectFromUnknown(parsed['errors']);
    if (direct.length) return direct;

    // Nested arrays we have seen in some payloads
    const dataObj = asRecord(parsed['data']);
    const nestedData = collectFromUnknown(dataObj?.['errors']);
    if (nestedData.length) return nestedData;

    // If message contains JSON again, recurse
    const messageValue = parsed['message'];
    if (typeof messageValue === 'string') {
      try {
        return extractAnvaErrors(messageValue);
      } catch {
        // fall through to text splitting below
      }
    }

    // Fallback: stringify object and split by newlines/bullets
    return (JSON.stringify(parsed) || '')
      .split(/\n|•|-\s/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  } catch {
    // Not JSON; split by lines and common bullet prefixes
    return errorString
      .split(/\n|•|-\s/) // handle dashes and bullets
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
};

const AnvaQuoteInlineError: FC<Props> = ({
  quoteName,
  error,
  title,
  introKey,
  listTitleKey,
  contactKey,
}) => {
  const { t } = useTranslation();
  const toast = useToast();

  const errorLines = useMemo(() => extractAnvaErrors(error), [error]);
  const copyText = useMemo(() => errorLines.join('\n'), [errorLines]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(copyText || (error ?? ''));
      toast({
        title: t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD_SUCCESS'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD_FAILED'),
        description: t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD_MANUAL-INSTRUCTION'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack align="stretch" spacing="3" px="5" pb="4">
      <Box>
        <Alert status="error">
          <AlertIcon />
          <VStack align="stretch" spacing={1} flex={1}>
            {title ? (
              <AlertTitle>{title}</AlertTitle>
            ) : (
              <AlertTitle>
                {t('FLOWS_PRODUCTS_QUOTE_ERROR_UNABLE-TO-CALCULATE')}
                {quoteName ? ` ${quoteName}` : ''}
              </AlertTitle>
            )}
            <AlertDescription>
              {t(introKey || 'FLOWS_PRODUCTS_QUOTE_ANVA-ERROR-INTRO')}
            </AlertDescription>
          </VStack>
        </Alert>
      </Box>

      <HStack justify="space-between" align="center">
        <Text fontWeight="semibold">
          {t(listTitleKey || 'FLOWS_PRODUCTS_QUOTE_ANVA-ERROR-LIST-TITLE')}
        </Text>
        <HStack spacing="1">
          <IconButton
            aria-label={t('GENERAL_ACTIONS_COPY-TO-CLIPBOARD')}
            size="sm"
            icon={<Icon name="GeneralActionsDuplicate" />}
            onClick={copyToClipboard}
            variant="ghost"
          />
        </HStack>
      </HStack>

      {errorLines.length > 0 ? (
        <UnorderedList pl="5" spacing={1} stylePosition="outside">
          {errorLines.map((line, idx) => (
            <ListItem key={`${line}-${idx}`}>
              <Text fontSize="sm">{line}</Text>
            </ListItem>
          ))}
        </UnorderedList>
      ) : (
        <Box
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Code p={3} whiteSpace="pre-wrap" width="100%" display="block">
            {error || ''}
          </Code>
        </Box>
      )}

      <Text fontSize="sm" color="gray.500">
        {t(contactKey || 'FLOWS_PRODUCTS_QUOTE_ANVA-ERROR-CONTACT')}
      </Text>
    </VStack>
  );
};

export default AnvaQuoteInlineError;
